import { useEffect, useMemo, useState } from "react";
import type {
  Asset,
  AssetDraft,
  AssetStatus,
  AssetType,
  ClientOption,
} from "../../types";
import { formatDate, getUpcomingAlerts, nearestService } from "../../utils/assetDates";
import AssetModal from "./AssetModal";
import AssetTable from "./AssetTable";
import { createBlankAsset, STATUSES } from "./assetConfig";
import ClientCards from "./ClientCards";
import styles from "./ClientAssetModule.module.css";

export type ClientAssetModuleProps = {
  type: AssetType;
  themeClassName: string;
  assets: Asset[];
  clients: ClientOption[];
  loading: boolean;
  error: string;
  requestedClientId?: string | null;
  onSave: (asset: Asset | AssetDraft) => Promise<boolean>;
  onDelete: (asset: Asset) => Promise<void>;
  onReload: () => void;
};

type EditableAsset = Asset | AssetDraft;
type StatusFilter = AssetStatus | "All status";

export default function ClientAssetModule({
  type,
  themeClassName,
  assets,
  clients,
  loading,
  error,
  requestedClientId,
  onSave,
  onDelete,
  onReload,
}: ClientAssetModuleProps) {
  const [selectedClientId, setSelectedClientId] = useState<string | null>(
    requestedClientId ?? null,
  );
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<StatusFilter>("All status");
  const [modal, setModal] = useState<EditableAsset | null>(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (requestedClientId) setSelectedClientId(requestedClientId);
  }, [requestedClientId]);

  const moduleAssets = useMemo(
    () => assets.filter((asset) => asset.type === type),
    [assets, type],
  );

  const clientOptions = useMemo(() => {
    if (clients.length) return [...clients].sort((a, b) => a.name.localeCompare(b.name));
    const unique = new Map<string, ClientOption>();
    moduleAssets.forEach((asset) => {
      unique.set(asset.clientId, {
        id: asset.clientId,
        code: asset.clientCode,
        name: asset.clientName,
      });
    });
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [clients, moduleAssets]);

  const selectedClient = clientOptions.find(
    (client) => client.id === selectedClientId,
  );
  const selectedAssets = moduleAssets.filter(
    (asset) => asset.clientId === selectedClientId,
  );
  const filteredAssets = selectedAssets.filter((asset) => {
    const matchesStatus =
      statusFilter === "All status" || asset.status === statusFilter;
    const haystack = [
      asset.assetId,
      asset.name,
      asset.vessel,
      asset.location,
      asset.manufacturer,
      asset.serial,
      asset.imoNumber,
      asset.model,
    ]
      .join(" ")
      .toLowerCase();
    return matchesStatus && haystack.includes(query.trim().toLowerCase());
  });

  const overall = {
    total: moduleAssets.length,
    clients: new Set(moduleAssets.map((asset) => asset.clientId)).size,
    dueSoon: moduleAssets.filter((asset) => asset.status === "Due Soon").length,
    attention: moduleAssets.filter((asset) => asset.status !== "Operational").length,
  };
  const clientOperational = selectedAssets.filter(
    (asset) => asset.status === "Operational",
  ).length;
  const clientAlerts = getUpcomingAlerts(selectedAssets);
  const clientReadiness = selectedAssets.length
    ? Math.round((clientOperational / selectedAssets.length) * 100)
    : 0;
  const nearest = selectedAssets
    .map((asset) => nearestService(asset))
    .filter(Boolean)
    .sort((left, right) => left!.date.localeCompare(right!.date))[0];

  function selectClient(clientId: string) {
    setSelectedClientId(clientId);
    setQuery("");
    setStatusFilter("All status");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function openCreate() {
    const target =
      selectedClient ?? clientOptions.find((client) => client.id === selectedClientId) ?? clientOptions[0];
    setViewOnly(false);
    setModal(createBlankAsset(type, target));
  }

  async function submitAsset(asset: EditableAsset) {
    setSaving(true);
    try {
      if (await onSave(asset)) {
        setModal(null);
        setViewOnly(false);
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className={`${styles.module} ${themeClassName}`}>
      <header className={styles.banner}>
        <div className={styles.bannerContent}>
          <p>{type.toUpperCase()} MODULE</p>
          <h1>{selectedClient ? selectedClient.name : `${type} Client Dashboard`}</h1>
          <span>
            {selectedClient
              ? `Showing ${type.toLowerCase()} records for ${selectedClient.name}.`
              : `Choose a client container to open its ${type.toLowerCase()} dashboard.`}
          </span>
        </div>
        <div className={styles.bannerActions}>
          {selectedClient && (
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setSelectedClientId(null)}
            >
              ← All clients
            </button>
          )}
          <button
            type="button"
            className={styles.primary}
            onClick={openCreate}
            disabled={clientOptions.length === 0}
          >
            <span aria-hidden="true">+</span> Add {type}
          </button>
        </div>
      </header>

      {error && (
        <div className={styles.error} role="alert">
          <div>
            <strong>Could not load data</strong>
            <span>{error}</span>
          </div>
          <button type="button" onClick={onReload}>
            Try again
          </button>
        </div>
      )}

      {!selectedClient ? (
        <>
          <div className={styles.statistics} aria-label={`${type} statistics`}>
            <article>
              <span>Total records</span>
              <strong>{overall.total}</strong>
            </article>
            <article>
              <span>Client containers</span>
              <strong>{overall.clients}</strong>
            </article>
            <article>
              <span>Due soon</span>
              <strong>{overall.dueSoon}</strong>
            </article>
            <article>
              <span>Attention</span>
              <strong>{overall.attention}</strong>
            </article>
          </div>

          <section className={styles.clientSection}>
            <header className={styles.sectionHeader}>
              <div>
                <p>CLIENT CONTAINERS</p>
                <h2>Select a client</h2>
                <span>
                  Each container opens a client-only dashboard and asset list.
                </span>
              </div>
              <b>{clientOptions.length} available</b>
            </header>
            {loading ? (
              <div className={styles.empty}>Loading client containers…</div>
            ) : clientOptions.length ? (
              <ClientCards
                type={type}
                clients={clientOptions}
                assets={moduleAssets}
                onSelect={selectClient}
              />
            ) : (
              <div className={styles.empty}>
                <strong>No clients available</strong>
                <span>Add an active client before creating asset records.</span>
              </div>
            )}
          </section>
        </>
      ) : (
        <>
          <div className={styles.clientBreadcrumb}>
            <button type="button" onClick={() => setSelectedClientId(null)}>
              Client containers
            </button>
            <span>/</span>
            <strong>{selectedClient.name}</strong>
          </div>

          <div className={styles.statistics} aria-label="Client statistics">
            <article>
              <span>Total assets</span>
              <strong>{selectedAssets.length}</strong>
            </article>
            <article>
              <span>Operational</span>
              <strong>{clientOperational}</strong>
            </article>
            <article>
              <span>Readiness</span>
              <strong>{clientReadiness}%</strong>
            </article>
            <article>
              <span>Email window</span>
              <strong>{clientAlerts.length}</strong>
            </article>
          </div>

          <aside className={styles.alertStrip}>
            <span className={styles.alertIcon}>✉</span>
            <div>
              <strong>Automatic service reminder</strong>
              <span>
                Enabled profiles receive one email in the 14-day window and one
                in the 7-day window. Nearest service: {formatDate(nearest?.date)}.
              </span>
            </div>
            <b>{clientAlerts.length} upcoming</b>
          </aside>

          <section className={styles.register}>
            <header className={styles.registerHeader}>
              <div>
                <p>{selectedClient.code} ASSET REGISTER</p>
                <h2>{type} Records</h2>
                <span>
                  Only assets belonging to {selectedClient.name} are shown.
                </span>
              </div>
            </header>

            <div className={styles.filterToolbar}>
              <label className={styles.control}>
                <span>Search records</span>
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={`Search ${type.toLowerCase()} records…`}
                />
              </label>
              <label className={`${styles.control} ${styles.selectControl}`}>
                <span>Asset status</span>
                <select
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as StatusFilter)
                  }
                >
                  <option>All status</option>
                  {STATUSES.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
              </label>
              <div className={styles.recordCount} aria-live="polite">
                <strong>{filteredAssets.length}</strong>
                <span>{filteredAssets.length === 1 ? "record" : "records"}</span>
              </div>
              <button
                type="button"
                className={styles.clearButton}
                onClick={() => {
                  setQuery("");
                  setStatusFilter("All status");
                }}
                disabled={!query && statusFilter === "All status"}
              >
                Clear filters
              </button>
            </div>

            {loading ? (
              <div className={styles.empty}>Loading records…</div>
            ) : filteredAssets.length ? (
              <AssetTable
                type={type}
                assets={filteredAssets}
                onView={(asset) => {
                  setViewOnly(true);
                  setModal(asset);
                }}
                onEdit={(asset) => {
                  setViewOnly(false);
                  setModal(asset);
                }}
                onDelete={(asset) => void onDelete(asset)}
              />
            ) : (
              <div className={styles.empty}>
                <strong>No matching records</strong>
                <span>Change the filters or add a new {type.toLowerCase()}.</span>
              </div>
            )}
          </section>
        </>
      )}

      {modal && (
        <AssetModal
          key={"id" in modal ? modal.id : `new-${modal.clientId}`}
          type={type}
          asset={modal}
          clients={clientOptions}
          viewOnly={viewOnly}
          saving={saving}
          onSetEditable={() => setViewOnly(false)}
          onClose={() => {
            if (!saving) setModal(null);
          }}
          onSubmit={submitAsset}
        />
      )}
    </section>
  );
}
